import styles from './form.module.scss';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import {
  CALENDLY_BOOKING_URL,
  config,
  defaults,
  FormValues,
} from '@components/shared/forms/tech-due-diligence-form/form.config';
import { Spinner } from '@components/shared/miscellaneous/spinner/spinner';
import Grecaptcha from '@libs/integrations/grecaptcha';
import FormSubmitStatus from '@components/shared/forms/form-submit-status/form-submit-status';
import { LeadMagnetStories } from '@models/leadmagnet.types';
import { createLeadMagnet } from '@services/leadmagnet.service';
import { ButtonHandshkaeGreen } from '@components/shared/buttons/button-handshake-green';
import { openCalendlyPopup, preloadCalendlyAssets } from '@libs/integrations/calendly';
import { splitFullName } from '@helpers/split-full-name.helper';
import GuidedFormField, { RequiredFieldName } from '@components/shared/forms/tech-due-diligence-form/guided-form-field';

type TechDueDiligenceFormProps = {
  story: LeadMagnetStories;
  title?: string;
  className?: string;
  buttonLabel?: string;
  disableSubmit?: boolean;
  initialValues?: FormValues;
  onSubmit?: (values: FormValues) => void;
};

export type TechDueDiligenceFormHandle = {
  submit: () => void;
};

type SubmitStatus = 'idle' | 'success' | 'failure';

const REQUIRED_FIELD_ORDER: RequiredFieldName[] = ['fullName', 'email'];

const TechDueDiligenceForm = forwardRef<TechDueDiligenceFormHandle, TechDueDiligenceFormProps>(
  (
    {
      story,
      title = defaults.title,
      className = defaults.className,
      buttonLabel = defaults.buttonLabel,
      disableSubmit = false,
      initialValues,
      onSubmit,
    },
    ref,
  ) => {
    const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
    const [guidedField, setGuidedField] = useState<RequiredFieldName | null>(null);
    const formikRef = useRef<FormikProps<FormValues>>(null);
    const fullNameInputRef = useRef<HTMLInputElement>(null);
    const emailInputRef = useRef<HTMLInputElement>(null);
    const fieldInputRefs = { fullName: fullNameInputRef, email: emailInputRef };

    const goToNextRequiredField = async (formik: FormikProps<FormValues>): Promise<boolean> => {
      const errors = await formik.validateForm();
      const nextField = REQUIRED_FIELD_ORDER.find((field) => !formik.values[field].trim() && errors[field]);
      if (!nextField) {
        setGuidedField(null);
        return true;
      }
      setGuidedField(nextField);
      const input = fieldInputRefs[nextField].current;
      input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input?.focus();
      return false;
    };

    const handleSendRequest = async (formik: FormikProps<FormValues>) => {
      const isValid = await goToNextRequiredField(formik);
      if (isValid) {
        formik.submitForm();
      }
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        if (formikRef.current) {
          await handleSendRequest(formikRef.current);
        }
      },
    }));

    const onAfterSubmit = (success: boolean, actions: FormikHelpers<FormValues>): void => {
      actions.setSubmitting(false);
      actions.resetForm();
      setGuidedField(null);
      setSubmitStatus(success ? 'success' : 'failure');
    };

    const submitHandler = async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      if (disableSubmit) {
        actions.setSubmitting(false);
        onSubmit?.(values);
        return;
      }
      try {
        actions.setSubmitting(true);
        const { firstName, lastName } = splitFullName(values.fullName);
        await createLeadMagnet({ firstName, lastName, email: values.email, company: values.company, story });
        if (values.wantsCall) {
          const [calendlyFirstName, ...calendlyLastNameParts] = values.fullName.trim().split(/\s+/);
          openCalendlyPopup(CALENDLY_BOOKING_URL, {
            firstName: calendlyFirstName,
            lastName: calendlyLastNameParts.join(' '),
            email: values.email,
          });
        }
        onAfterSubmit(true, actions);
      } catch {
        onAfterSubmit(false, actions);
      }
    };

    return (
      <div className={`${styles.container} ${className}`}>
        {
          <>
            <Grecaptcha />
            <Formik
              innerRef={formikRef}
              onSubmit={submitHandler}
              {...config}
              initialValues={initialValues ?? config.initialValues}
            >
              {(formik) => {
                return (
                  <Form className={`${className} ${styles.form}`}>
                    {!disableSubmit && <h3 className={styles.form__title}>{title}</h3>}
                    <GuidedFormField
                      name="fullName"
                      label="Full name"
                      placeholder="Your full name"
                      formik={formik}
                      guidedField={guidedField}
                      inputRef={fullNameInputRef}
                    />
                    <GuidedFormField
                      name="email"
                      label="Email"
                      placeholder="your@email.com"
                      formik={formik}
                      guidedField={guidedField}
                      inputRef={emailInputRef}
                    />
                    <div className={styles.form__field}>
                      <label htmlFor="company">Company</label>
                      <input id="company" type="text" placeholder="Your company" {...formik.getFieldProps('company')} />
                    </div>
                    <div className={styles.form__checkbox}>
                      <input
                        id="wantsCall"
                        type="checkbox"
                        checked={formik.values.wantsCall}
                        {...formik.getFieldProps('wantsCall')}
                        onChange={(event) => {
                          formik.getFieldProps('wantsCall').onChange(event);
                          if (event.target.checked) {
                            preloadCalendlyAssets();
                          }
                        }}
                      />
                      <label htmlFor="wantsCall">Book a 30 min free call with our expert</label>
                    </div>
                    {!disableSubmit && (
                      <ButtonHandshkaeGreen
                        text={buttonLabel}
                        action={() => handleSendRequest(formik)}
                        className={styles.form__button}
                      />
                    )}
                    {formik.isSubmitting ? <Spinner className={styles.preloader} /> : ''}
                    {submitStatus !== 'idle' && (
                      <div className={styles.success}>
                        <FormSubmitStatus
                          status={submitStatus === 'success' ? 'success' : 'failure'}
                          customSuccess={{
                            title: 'Done!',
                            text: 'Thank you for filling out the form. Please, check your email for the file!',
                          }}
                          customFailure={{
                            title: 'Something went wrong',
                            text: 'We could not send the checklist. Please try again later.',
                          }}
                          theme="dark"
                          className={styles.statusContainer}
                        />
                      </div>
                    )}
                  </Form>
                );
              }}
            </Formik>
          </>
        }
      </div>
    );
  },
);

TechDueDiligenceForm.displayName = 'TechDueDiligenceForm';

export default TechDueDiligenceForm;
